import { createSource } from '@evertrue/et-flux';
import Api from 'entities/helpers/api';

const SubscriptionPrefSource = createSource('SubscriptionPrefSource', {
  actions: {
    fetchedPreferences(preferences) {
      this.require(Array.isArray(preferences), 'preferences should be an array');
    },

    // Dispatched when the initial preferences fetch fails, so SubscriptionPrefStore
    // isn't stuck waiting on a fetch that will never resolve (SIG-853).
    failedPreferences() {},

    updatedPreference(preference) {
      this.require(typeof preference === 'object', 'preference should be an object');
    },

    deletedPreference(key) {
      this.require(typeof key === 'string', 'key should be string');
    },
  },

  formatPreferences(prefs) {
    return {
      ...prefs,
      email_frequency: prefs.email_frequency?.toLowerCase(),
      push_frequency: prefs.push_frequency?.toLowerCase(),
    };
  },

  api: {
    fetch() {
      const req = Api.EMS.PREFS.get({
        disableAlerts: true,
        success: (resp) => {
          // `fetchedPreferences` requires an array and fails validation (silently,
          // via a console.warn -- it does not throw) if it isn't one, which would
          // leave `loaded` stuck false just like an outright request failure. Guard
          // against a malformed success response (e.g. `{ preferences: {} }`) the
          // same way we guard against a rejected request.
          const preferences = Array.isArray(resp) ? resp : Array.isArray(resp?.preferences) ? resp.preferences : [];
          this.actions.fetchedPreferences(preferences);
        },
      });
      // This used to swallow failures entirely, leaving `loaded` false forever
      // with no signal that anything went wrong (SIG-853) — report it and let
      // the store fail safe (both-disabled defaults) instead of hanging.
      req?.catch?.((error) => {
        window.Raven?.captureMessage('SubscriptionPrefSource fetch failed', { extra: { error } });
        this.actions.failedPreferences();
      });
      return req;
    },

    update(key, data) {
      return Api.EMS.PREFS.put({
        urlExtend: `/${key}`,
        data: JSON.stringify(this.formatPreferences(data)),
        success: (pref) => {
          this.actions.updatedPreference(pref[0]);
        },
      });
    },

    delete(key) {
      return Api.EMS.PREFS.delete({
        urlExtend: `/${key}`,
        disableAlerts: true,
        success: () => {
          this.actions.deletedPreference(key);
        },
      });
    },
  },
});

export default SubscriptionPrefSource;
